import { Slottable } from '@radix-ui/react-slot';
import { ForwardedRef, forwardRef, MouseEvent, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../core';
import { getExtension, getFileType } from '../../utils/file_type';
import { useText } from '../../utils/hooks/use_text';
import { UNSPECIFIED_ERROR, unwrapCallbackValue } from '../../utils/unwrap_value';
import { AutoFocus } from '../auto_focus';
import { theme as commonTheme } from '../common.theme';
import { HelpText } from '../form';
import { theme as commonInputTheme } from '../form/common_input.theme';
import { IconDictionary } from '../icon';
import { theme as iconTheme } from '../icon/icon.theme';
import { IconButton } from '../icon_button';
import { Spacer } from '../layout';
import { NotificationAlert } from '../notification_alert';
import { Spinner } from '../spinner';
import { theme } from './attachment.theme';
import { AttachmentFileName } from './attachment_filename.component';
import { AttachmentPreview } from './attachment_preview.component';

const elements = {
	root: styled.div<{ $compactMode: boolean; $size: AttachmentSizes }>`
		.cellula-attachment__container {
			position: relative;
			display: flex;
			align-items: stretch;
			justify-content: stretch;
		}

		.cellula-attachment__button {
			background: ${theme.getValue('baseBackground')};
			border: 1px solid ${theme.getValue('baseBorderColor')};
			border-radius: ${theme.getValue('baseBorderRadius')};
			box-shadow: ${theme.getValue('baseBoxShadow')};
			padding: ${theme.getValue('basePadding')};
			transition: box-shadow 100ms ease-in;
			display: flex;
			gap: ${coreTokens.spacing.x1_5};
			${({ $compactMode }) => ($compactMode ? '' : 'width: 100%;')};

			&:focus-visible {
				outline: 2px solid ${commonTheme.getValue('focusColor')};
				outline-offset: 2px;
			}
		}

		.cellula-attachment__preview {
			width: ${({ $size }) => ($size === 'small' ? '40px' : '72px')};
			height: ${({ $size }) => ($size === 'small' ? '40px' : '72px')};
			flex-shrink: 0;
			position: relative;
		}

		.cellula-attachment__preview-inner {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			height: 100%;
			transition: all 100ms ease-in;
			border-radius: ${theme.getValue('baseBorderRadius')};
			color: ${theme.getValue('previewIconColor')};
			overflow: hidden;

			&[data-background='true'] {
				background: ${theme.getValue('previewBackground')};
			}
		}

		.cellula-attachment__preview-spinner {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.cellula-attachment__preview-image {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		.cellula-attachment__content {
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
			align-items: flex-start;
			gap: ${coreTokens.spacing.x0_5};
			flex: 1 1 auto;
			word-break: break-all;
			text-align: left;
			transition: opacity 100ms ease-in;
		}

		.cellula-attachment__content-name {
			position: relative;
			width: 100%;
			min-height: 1.125rem;
			font: ${theme.getValue('nameFont')};
			color: ${theme.getValue('nameFontColor')};
		}

		.cellula-attachment__content-meta {
			font: ${theme.getValue('metaFont')};
			color: ${theme.getValue('metaFontColor')};
		}

		.cellula-attachment__delete-button {
			position: absolute;
			top: calc(${theme.getValue('basePadding')} / 2);
			right: calc(${theme.getValue('basePadding')} / 2);
		}

		&[data-loading='true'] {
			.cellula-attachment__button {
				cursor: progress;
			}

			.cellula-attachment__preview-inner {
				filter: blur(2px);
				opacity: 0.6;
			}

			.cellula-attachment__content {
				opacity: 0.7;
			}
		}

		&:not([data-loading='true']) {
			.cellula-attachment__button {
				@media (hover: hover) {
					&:hover {
						box-shadow: ${theme.getValue('hoverBoxShadow')};
					}
				}

				&:active {
					box-shadow: none;
				}
			}
		}

		&[data-delete-button='true'] {
			.cellula-attachment__content {
				padding-right: calc(${iconTheme.getValue('mediumSize')} + ${coreTokens.spacing.x1});
			}
		}

		&[data-invalid='true'] {
			.cellula-attachment__button {
				border-color: ${commonInputTheme.getValue('invalidBorderColor')};
			}
		}
	`,
};

type AttachmentSizes = 'small' | 'medium';

interface AttachmentProps {
	name: string;
	compactMode?: boolean;
	size?: AttachmentSizes;
	meta?: string;
	icon?: keyof IconDictionary;
	preview?: string | JSX.Element;
	loading?: boolean;
	onClick?: (event: MouseEvent) => void;
	onDelete?: (event: MouseEvent) => void | Promise<void>;
	deleteTooltip?: string;
	invalid?: boolean;
	invalidText?: string;
}

function CellulaAttachment(
	{
		name,
		compactMode = false,
		size = 'small',
		meta,
		icon = 'document',
		preview,
		loading,
		onClick,
		onDelete,
		deleteTooltip,
		invalid,
		invalidText,
	}: AttachmentProps,
	ref: ForwardedRef<HTMLDivElement>,
): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const buttonRef = useRef<HTMLButtonElement>(null);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const deleteButtonRef = useRef<HTMLButtonElement>(null);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const staticTexts = useText();
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const id = useId();
	const helpTextId = `cellula-attachment-help-text-${id}`;
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const [isDeleting, setIsDeleting] = useState(false);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const [deleteError, setDeleteError] = useState<boolean | string>(false);
	const isLoading = loading || isDeleting;

	let fileType;
	if (getExtension(name)) {
		fileType = getFileType(name);
	}

	function handleClick(event: MouseEvent<HTMLButtonElement>): void {
		if (isLoading) {
			return;
		}

		onClick?.(event);
	}

	async function handleDelete(event: MouseEvent<HTMLButtonElement>): Promise<void> {
		if (isDeleting) {
			return;
		}

		setIsDeleting(true);

		if (onDelete) {
			const result = await unwrapCallbackValue(onDelete(event));
			if (result.err) {
				setDeleteError(result.err);
			}
		}

		setIsDeleting(false);
	}

	return (
		<elements.root
			ref={ref}
			data-loading={isLoading || undefined}
			data-invalid={invalid ? 'true' : undefined}
			data-delete-button={onDelete ? 'true' : undefined}
			$compactMode={compactMode}
			$size={size}
		>
			<div className='cellula-attachment__container'>
				<Slottable>
					{/* aria-invalid is not officially supported for button. But screen readers do read it out.
					    So.. we will add it here because there is not other good way of indicate that this
							attachment is invalid. */}
					{/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
					<button
						type='button'
						className='cellula-attachment__button'
						aria-busy={loading ?? undefined}
						onClick={handleClick}
						aria-invalid={invalid ? 'true' : undefined}
						aria-describedby={helpTextId}
						ref={buttonRef}
					>
						<div className='cellula-attachment__preview'>
							<AttachmentPreview fileType={fileType} preview={preview} icon={icon} />
							{isLoading ? (
								<div className='cellula-attachment__preview-spinner'>
									<Spinner size='medium' />
								</div>
							) : null}
						</div>
						{!compactMode ? (
							<div className='cellula-attachment__content'>
								<div className='cellula-attachment__content-name'>
									{fileType !== undefined ? <AttachmentFileName>{name}</AttachmentFileName> : name}
								</div>
								{meta ? <div className='cellula-attachment__content-meta'>{meta}</div> : null}
							</div>
						) : null}
					</button>
				</Slottable>
				{onDelete ? (
					<div className='cellula-attachment__delete-button'>
						<IconButton
							icon='cross'
							size='medium'
							variant='neutral'
							text={deleteTooltip ?? staticTexts('Component.Attachment.DeleteTooltip', { name })}
							onClick={handleDelete}
							ref={deleteButtonRef}
							loading={isDeleting}
						/>
					</div>
				) : null}
			</div>
			{invalid && invalidText ? (
				<HelpText invalid id={helpTextId}>
					{invalidText}
				</HelpText>
			) : null}
			{deleteError ? (
				<>
					<Spacer vertical={coreTokens.spacing.x1} />
					<NotificationAlert variant='danger' closable onClose={() => setDeleteError(false)}>
						<AutoFocus>
							{deleteError === UNSPECIFIED_ERROR ? staticTexts('Component.Attachment.DeleteUnspecifiedError') : deleteError}
						</AutoFocus>
					</NotificationAlert>
				</>
			) : null}
		</elements.root>
	);
}

const Attachment = forwardRef(CellulaAttachment);

export { Attachment };

Attachment.displayName = 'CellulaAttachment';
